import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { useRef } from "react";
import { useBreakpoint } from "@/lib/breakpoints";
import { cn } from "@/lib/utils";
import NavigationLink from "./NavigationLink";
import VariableImage from "./ui/VariableImage";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const FullSection = ({
  img,
  title,
  text,
  link,
  imgPosition,
}: {
  img: {
    url: string;
    alternativeText: string;
    caption: string;
  };
  title: string;
  text: string;
  link?: any;
  imgPosition: string;
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = useBreakpoint();

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 85%",
          end: "+=600px",
        },
      });

      tl.from("#fullSectionTitle,#fullSectionText", {
        autoAlpha: 0,
        y: 30,
      });
      tl.from(
        "#fullSectionImg",
        {
          clipPath: isDesktop ? "inset(100% 0 0 0)" : "none",
          scale: 1.05,
        },
        "<30%",
      );
    },
    { scope: sectionRef, dependencies: [isDesktop], revertOnUpdate: true },
  );

  return (
    <section ref={sectionRef}>
      <div className="container">
        <div className="flex w-full flex-wrap items-end justify-between gap-10 lg:flex-nowrap">
          <h2
            id="fullSectionTitle"
            className={cn(
              "title-50 uppercase! grow",
              text ? "lg:basis-1/2" : "",
            )}
          >
            <div className="seperator border-black pb-10"></div>
            {title}
          </h2>
          {text !== "" && (
            <div
              id="fullSectionText"
              className={cn(text ? "lg:basis-1/2" : "")}
            >
              <p className="whitespace-pre-line">{text}</p>
              {link && (
                <NavigationLink
                  target={link.NewTab ? "_blank" : "_self"}
                  href={link.LinkTo}
                  className="mt-5 block font-semibold capitalize underline"
                >
                  {link.Text}
                </NavigationLink>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="relative mx-auto mt-8 aspect-16/7 max-h-[1000px] w-full max-w-[calc(100%-40px)] overflow-hidden lg:mt-16">
        <VariableImage
          id="fullSectionImg"
          src={img.url}
          className={cn("object-cover", imgPosition)}
          fill={true}
          sizes="100vw"
          alt={img.alternativeText}
          caption={img.caption}
        />
      </div>
    </section>
  );
};

export default FullSection;
